export interface RawDataColumn {
  accessor?: string;
  Header?: string;
  header?: string;
}

export interface RawDataEntry {
  table?: RawDataTable;
}

export type RawDataRow = Record<string, unknown>;

export interface RawDataTable {
  columns?: RawDataColumn[];
  data?: RawDataRow[];
}

export interface TurfAdapterResult {
  completeRowCount: number;
  items: TurfItem[];
  respondents: TurfRespondent[];
  totalRowCount: number;
}

export interface TurfItem {
  id: string;
  label: string;
  questionText: string;
  rawAccessor: string;
  rawHeader: string;
  special: boolean;
}

export interface TurfQuestion {
  answers?: TurfQuestionOption[];
  id?: number | string;
  options?: TurfQuestionOption[];
  richText?: null | string;
  text?: null | string;
  title?: null | string;
  turf?: boolean;
}

export interface TurfQuestionOption {
  id?: number | string;
  isExclusive?: boolean;
  isNoneOfTheAbove?: boolean;
  isOther?: boolean;
  optionSourceId?: null | number | string;
  text?: null | string;
  title?: null | string;
  value?: null | string;
}

export interface TurfRespondent {
  id: string;
  rawRow: RawDataRow;
  selectedItemIds: Set<string>;
}

interface ParsedOptionHeader {
  optionLabel: string;
  questionText: string;
}

const COMPLETE_STATUS = 'complete';
const SAFE_TURF_ITEM_ID_PATTERN = /^-?[A-Za-z0-9_-]+$/;
const WRITE_IN_LABEL = 'write-in';

export const getColumnHeader = (column: RawDataColumn): string =>
  String(column.Header ?? column.header ?? '').trim();

const stripHtml = (value: string): string => value.replace(/<[^>]*>/g, ' ');

export const normalizeComparableText = (value: unknown): string =>
  stripHtml(String(value ?? ''))
    .replace(/&nbsp;/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

const getOptionLabel = (option: TurfQuestionOption): string =>
  String(option.title ?? option.text ?? option.value ?? '').trim();

const getQuestionText = (question: TurfQuestion): string =>
  String(question.text ?? question.title ?? question.richText ?? '').trim();

export const isTurfQuestion = (question: TurfQuestion): boolean => question.turf === true;

export const getTurfQuestions = <T extends TurfQuestion>(questions: T[] = []): T[] =>
  questions.filter(isTurfQuestion);

export const parseRawOptionHeader = (header: string): null | ParsedOptionHeader => {
  const segments = header.split('|').map((segment) => segment.trim());

  if (segments.length < 2) return null;

  const optionLabel = segments[segments.length - 1];
  const questionText = segments.slice(0, -1).join(' | ');

  if (!optionLabel || !questionText) return null;

  return { optionLabel, questionText };
};

export const isWriteInLabel = (label: string): boolean =>
  normalizeComparableText(label) === WRITE_IN_LABEL;

export const isSpecialLabel = (label: string): boolean => {
  const normalizedLabel = normalizeComparableText(label);

  return (
    normalizedLabel === 'other' ||
    normalizedLabel.includes('please specify') ||
    normalizedLabel.startsWith('other write-in') ||
    normalizedLabel.startsWith('other write in') ||
    normalizedLabel.startsWith('none of the above')
  );
};

const hashString = (value: string): string => {
  let hash = 0;

  for (let index = 0; index < value.length; index += 1) {
    hash = (hash * 31 + value.charCodeAt(index)) >>> 0;
  }

  return hash.toString(36);
};

export const getTurfItemId = (rawAccessor: string): string => {
  if (SAFE_TURF_ITEM_ID_PATTERN.test(rawAccessor)) return rawAccessor;

  return `turf_${hashString(normalizeComparableText(rawAccessor))}`;
};

const getOptionMetadataByLabel = (question: TurfQuestion): Map<string, TurfQuestionOption> => {
  const options = question.answers ?? question.options ?? [];

  return options.reduce((metadata, option) => {
    const label = getOptionLabel(option);

    if (label) metadata.set(normalizeComparableText(label), option);

    return metadata;
  }, new Map<string, TurfQuestionOption>());
};

const getIsSpecialFromMetadata = (option: TurfQuestionOption | undefined, label: string): boolean =>
  Boolean(
    option?.isOther ||
    option?.isNoneOfTheAbove ||
    (option?.isExclusive && normalizeComparableText(label).startsWith('none of the above')) ||
    isSpecialLabel(label)
  );

export const getRawDataTable = (rawData: RawDataEntry[] = []): RawDataTable | undefined =>
  rawData[0]?.table;

export const buildTurfItemsForQuestion = ({
  columns = [],
  question,
}: {
  columns?: RawDataColumn[];
  question: TurfQuestion;
}): TurfItem[] => {
  const questionText = getQuestionText(question);
  const normalizedQuestionText = normalizeComparableText(questionText);
  const optionMetadataByLabel = getOptionMetadataByLabel(question);

  if (!normalizedQuestionText) return [];

  return columns.reduce<TurfItem[]>((items, column) => {
    const rawAccessor = column.accessor != null ? String(column.accessor) : '';
    const rawHeader = getColumnHeader(column);
    const parsedHeader = parseRawOptionHeader(rawHeader);

    if (!rawAccessor || !parsedHeader) return items;
    if (normalizeComparableText(parsedHeader.questionText) !== normalizedQuestionText) return items;
    if (isWriteInLabel(parsedHeader.optionLabel)) return items;

    const optionMetadata = optionMetadataByLabel.get(
      normalizeComparableText(parsedHeader.optionLabel)
    );

    items.push({
      id: getTurfItemId(rawAccessor),
      label: parsedHeader.optionLabel,
      questionText: parsedHeader.questionText,
      rawAccessor,
      rawHeader,
      special: getIsSpecialFromMetadata(optionMetadata, parsedHeader.optionLabel),
    });

    return items;
  }, []);
};

export const isCompleteRow = (row: RawDataRow): boolean =>
  row.status == null ||
  String(row.status).trim() === '' ||
  normalizeComparableText(row.status) === COMPLETE_STATUS;

export const isItemSelected = (row: RawDataRow, item: TurfItem): boolean =>
  String(row[item.rawAccessor] ?? '').trim() !== '';

export const buildTurfRespondents = ({
  items,
  rows = [],
}: {
  items: TurfItem[];
  rows?: RawDataRow[];
}): TurfRespondent[] =>
  rows.filter(isCompleteRow).map((row, index) => ({
    id: String(row.user_id ?? row.response_id ?? index),
    rawRow: row,
    selectedItemIds: new Set(items.filter((item) => isItemSelected(row, item)).map(({ id }) => id)),
  }));

export const adaptRawDataForTurfQuestion = ({
  question,
  rawData,
}: {
  question: TurfQuestion;
  rawData?: RawDataEntry[];
}): TurfAdapterResult => {
  const table = getRawDataTable(rawData);
  const rows = table?.data ?? [];
  const items = buildTurfItemsForQuestion({
    columns: table?.columns,
    question,
  });

  return {
    completeRowCount: rows.filter(isCompleteRow).length,
    items,
    respondents: buildTurfRespondents({ items, rows }),
    totalRowCount: rows.length,
  };
};
